/**
 * Default export pour l'auto-discovery `apps/web/src/routes/modules.ts`.
 *
 * Format imposé par `ModuleRoute` (cf. modules.ts ligne 14-26) :
 *   { path, label, icon, element }
 *
 * Le shell prend `element` et l'utilise dans <Outlet />. On lazy-load la page
 * pour ne pas charger TanStack Query dans le bundle initial (même pattern
 * que `herbo-kpi-entrepot/web/route.tsx`, gabarit désigné par la spec).
 *
 * Saisie en masse (mission W-F3, décision Ben 25/07 « les deux ») : la route
 * `conditionnement/atelier` (phase B, écran atelier plein écran) est
 * `hidden: true` — même pattern que `herbo-lot-detail/web/route.tsx` (liste +
 * fiche détail) : elle reste atteignable via les boutons « Ouvrir l'atelier
 * de saisie » (page + section Ordres) mais n'a pas son propre onglet
 * TopBar — un `export default` en TABLEAU expose les 2 routes top-level.
 */

import { lazy, Suspense, type ReactNode } from 'react';
import type { ModuleRoute } from "@herbo/module-route";

const Conditionnement = lazy(() => import('./pages/Conditionnement'));
const AtelierSaisie = lazy(() => import('./pages/AtelierSaisie'));
const ConstatSerie = lazy(() => import('./pages/ConstatSerie'));
const ConstatFiches = lazy(() => import('./pages/ConstatFiches'));
const SerieFiche = lazy(() => import('./pages/SerieFiche'));

function withSuspense(node: ReactNode, label: string): ReactNode {
  return (
    <Suspense
      fallback={<div className="px-6 py-8 text-sm text-slate-500">{label}</div>}
    >
      {node}
    </Suspense>
  );
}

const conditionnementRoute: ModuleRoute = {
  path: 'conditionnement',
  label: 'Conditionnement',
  icon: 'package',
  element: withSuspense(<Conditionnement />, 'Chargement du module Conditionnement…'),
};

const atelierRoute: ModuleRoute = {
  path: 'conditionnement/atelier',
  label: 'Conditionnement',
  icon: 'package',
  hidden: true,
  element: withSuspense(<AtelierSaisie />, 'Chargement de l’atelier de saisie…'),
};

/**
 * Écran de constat (3ᵉ TEMPS, découverte 16/08) et fiches à la demande —
 * routes CACHÉES, même pattern que `atelierRoute` : atteintes depuis le
 * wizard de validation (`ValiderOrdreWizard`) et la section Ordres, jamais
 * via un onglet TopBar dédié.
 */
const constatRoute: ModuleRoute = {
  path: 'conditionnement/constat/:id',
  label: 'Conditionnement',
  icon: 'package',
  hidden: true,
  element: withSuspense(<ConstatSerie />, 'Chargement du constat…'),
};

const constatFichesRoute: ModuleRoute = {
  path: 'conditionnement/constat/:id/fiches',
  label: 'Conditionnement',
  icon: 'package',
  hidden: true,
  element: withSuspense(<ConstatFiches />, 'Chargement des fiches…'),
};

/**
 * Fiche d'une série (lecture) — additif 30/09 : destination du bouton
 * « Ouvrir la série N » du signal rupture. Route CACHÉE, même pattern.
 */
const serieFicheRoute: ModuleRoute = {
  path: 'conditionnement/serie/:id',
  label: 'Conditionnement',
  icon: 'package',
  hidden: true,
  element: withSuspense(<SerieFiche />, 'Chargement de la série…'),
};

export default [conditionnementRoute, atelierRoute, constatRoute, constatFichesRoute, serieFicheRoute];
